<script setup lang="ts">
import { storeToRefs } from "pinia";
import { onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import { toast } from "vue-sonner";
import {
  cancelRedistribution,
  fetchRedistributionResult,
} from "../services/apiClient.ts";
import {
  applyCompletedRedistributionResult,
  hydrateTraceLogFromResult,
} from "../services/applyRedistributionResult.ts";
import {
  useRedistributionQueueStore,
  type QueuedRequest,
} from "../stores/redistributionQueueStore.ts";

const router = useRouter();
const queueStore = useRedistributionQueueStore();
const { items } = storeToRefs(queueStore);

// Tick every second to drive elapsed time display
const now = ref(Date.now());
let tickInterval: ReturnType<typeof setInterval> | undefined;
onMounted(() => {
  tickInterval = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});
onUnmounted(() => {
  if (tickInterval) clearInterval(tickInterval);
});

function elapsed(submittedAt: string, endTime?: number): string {
  const end = endTime ?? now.value;
  const ms = end - new Date(submittedAt).getTime();
  if (ms < 0) return "00:00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

async function loadResult(item: QueuedRequest): Promise<void> {
  if (item.status === "failed") {
    toast.error(item.errorMessage ?? "Request failed for an unknown reason.");
    return;
  }

  if (item.status === "cancelled") {
    toast.info("This request was cancelled.");
    return;
  }

  if (item.status !== "completed") return;

  try {
    const result = await fetchRedistributionResult(item.requestId);
    applyCompletedRedistributionResult(result, item.requestId);
    try {
      await hydrateTraceLogFromResult(result);
    } catch {
      // Trace log is optional — still navigate to Results.
    }

    await router.push({ name: "results" });
  } catch (error) {
    const msg =
      error instanceof Error ? error.message : "Failed to load result.";
    toast.error(msg);
  }
}

function dismiss(requestId: string): void {
  queueStore.removeItem(requestId);
}

async function cancelRequest(requestId: string): Promise<void> {
  try {
    await cancelRedistribution(requestId);
  } catch (error) {
    const msg =
      error instanceof Error ? error.message : "Failed to cancel request.";
    toast.error(msg);
  }
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}
</script>

<template>
  <aside
    v-if="items.length > 0"
    class="redistribution-queue-drawer"
    aria-label="Redistribution queue"
  >
    <h3 class="redistribution-queue-title">Redistribution Queue</h3>
    <ul class="redistribution-queue-list">
      <li v-for="item in items" :key="item.requestId">
        <button
          class="redistribution-queue-pill"
          :class="{
            'redistribution-queue-pill-running': item.status === 'running',
            'redistribution-queue-pill-completed': item.status === 'completed',
            'redistribution-queue-pill-failed': item.status === 'failed',
            'redistribution-queue-pill-cancelled': item.status === 'cancelled',
          }"
          :disabled="item.status === 'running' || item.status === 'cancelled'"
          :title="
            item.status === 'completed'
              ? 'Click to view results'
              : item.status === 'failed'
                ? (item.errorMessage ?? 'Request failed')
                : item.status === 'cancelled'
                  ? 'Request was cancelled'
                  : 'Running...'
          "
          @click="loadResult(item)"
        >
          <span class="redistribution-queue-pill-indicator" aria-hidden="true">
            {{
              item.status === "running"
                ? "⏳"
                : item.status === "completed"
                  ? "✅"
                  : item.status === "cancelled"
                    ? "🚫"
                    : "❌"
            }}
          </span>
          <span class="redistribution-queue-pill-content">
            <span class="redistribution-queue-pill-time">
              {{ formatTime(item.submittedAt) }}
              <span class="redistribution-queue-pill-status-label">
                {{
                  item.status === "running"
                    ? ""
                    : item.status === "completed"
                      ? "Completed"
                      : item.status === "cancelled"
                        ? "Cancelled"
                        : "Failed"
                }}
              </span>
            </span>
            <span
              v-if="item.status === 'running' || item.status === 'completed'"
              class="redistribution-queue-pill-elapsed"
            >
              Elapsed: {{ elapsed(item.submittedAt, item.completedAt) }}
            </span>
          </span>
          <button
            v-if="item.status === 'running'"
            class="redistribution-queue-pill-dismiss redistribution-queue-pill-cancel"
            title="Cancel request"
            @click.stop="cancelRequest(item.requestId)"
          >
            ×
          </button>
          <button
            v-else
            class="redistribution-queue-pill-dismiss"
            title="Dismiss"
            @click.stop="dismiss(item.requestId)"
          >
            ×
          </button>
        </button>
      </li>
    </ul>
  </aside>
</template>

<style>
.redistribution-queue-drawer {
  width: 14rem;
  padding: 0.75rem;
  border-left: 1px solid var(--color-slate-200, #e2e8f0);
  overflow-y: auto;
  flex-shrink: 0;
}

.redistribution-queue-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-slate-500, #64748b);
  margin: 0 0 0.5rem;
}

.redistribution-queue-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.redistribution-queue-pill {
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  width: 100%;
  padding: 0.5rem 0.625rem;
  border-radius: 0.5rem;
  border: 1px solid transparent;
  font-size: 0.75rem;
  cursor: pointer;
  transition:
    background-color 0.15s,
    border-color 0.15s;
  text-align: left;
}

.redistribution-queue-pill:disabled {
  cursor: default;
  opacity: 0.85;
}

.redistribution-queue-pill-running {
  background-color: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}

.redistribution-queue-pill-completed {
  background-color: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

.redistribution-queue-pill-completed:hover:not(:disabled) {
  background-color: #dcfce7;
}

.redistribution-queue-pill-failed {
  background-color: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}

.redistribution-queue-pill-cancelled {
  background-color: #f5f5f4;
  border-color: #d6d3d1;
  color: #57534e;
}

.redistribution-queue-pill-indicator {
  flex-shrink: 0;
  line-height: 1.25rem;
}

.redistribution-queue-pill-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.redistribution-queue-pill-time {
  font-weight: 500;
  white-space: nowrap;
}

.redistribution-queue-pill-status-label {
  margin-left: 0.25rem;
  font-weight: 400;
}

.redistribution-queue-pill-elapsed {
  font-variant-numeric: tabular-nums;
  font-size: 0.6875rem;
  opacity: 0.75;
  white-space: nowrap;
}

.redistribution-queue-pill-dismiss {
  flex-shrink: 0;
  width: 1.125rem;
  height: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: inherit;
  font-size: 0.875rem;
  line-height: 1;
  cursor: pointer;
  opacity: 0.6;
}

.redistribution-queue-pill-dismiss:hover {
  opacity: 1;
  background-color: rgb(0 0 0 / 8%);
}

.redistribution-queue-pill-cancel {
  opacity: 0.8;
  color: #dc2626;
  font-weight: 700;
}

.redistribution-queue-pill-cancel:hover {
  opacity: 1;
  background-color: rgb(220 38 38 / 12%);
}
</style>
